import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { Trash2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';

export default function CartPage() {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  
  useEffect(() => {
    loadCart();
  }, []);
  
  const loadCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCart(response.data);
      setIsGuest(false);
    } catch (error) {
      // If not logged in, check localStorage for guest cart
      const guestCart = localStorage.getItem('guestCart');
      if (guestCart) {
        setCart(JSON.parse(guestCart));
      }
      setIsGuest(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemoveItem = async (cartItemId, index) => {
    if (isGuest) {
      // Remove from localStorage
      const updatedCart = cart.filter((_, i) => i !== index);
      setCart(updatedCart);
      localStorage.setItem('guestCart', JSON.stringify(updatedCart));
      toast.success('Item removed from cart');
    } else {
      try {
        await api.delete(`/cart/${cartItemId}`);
        toast.success('Item removed from cart');
        loadCart();
      } catch (error) {
        toast.error('Failed to remove item');
      }
    }
  };
  
  const handleClearCart = async () => {
    if (isGuest) {
      localStorage.removeItem('guestCart');
      setCart([]);
      toast.success('Cart cleared');
    } else {
      try {
        await api.delete('/cart');
        toast.success('Cart cleared');
        setCart([]);
      } catch (error) {
        toast.error('Failed to clear cart');
      }
    }
  };
  
  const handleProceedToCheckout = () => {
    if (isGuest) {
      // Store cart in localStorage for guest checkout
      localStorage.setItem('guestCart', JSON.stringify(cart));
    }
    navigate(tenantPath('/checkout'));
  };
  
  const total = cart.reduce((sum, item) => sum + (item.total_price || 0), 0);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Header />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#4A6741] border-r-transparent"></div>
          <p className="mt-4 text-[#5A6B5C]">Loading cart...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <h1 
          className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-8"
          style={{ fontFamily: 'Chivo, sans-serif' }}
          data-testid="cart-title"
        >
          Shopping Cart
        </h1>
        
        {cart.length === 0 ? (
          <div className="text-center py-24" data-testid="empty-cart">
            <ShoppingBag className="h-16 w-16 text-[#5A6B5C] mx-auto mb-6" />
            <p className="text-[#5A6B5C] text-lg mb-8">Your cart is empty</p>
            <Link 
              to={tenantPath("/category/shafts")}
              className="inline-block h-12 px-8 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3A5733] transition-all"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cart.map((item, index) => (
                <div key={item.cart_item_id || index} className="bg-white p-6 rounded-lg border border-[#E8E4DC]" data-testid={`cart-item-${item.cart_item_id || index}`}>
                  <div className="flex items-start space-x-6">
                    <div className="w-24 h-24 bg-[#F5F3EF] rounded-lg flex-shrink-0 overflow-hidden">
                      {item.product_image ? (
                        <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#5A6B5C] text-xs">No image</div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#2C3E2D] mb-2">{item.product_name}</h3>
                      <div className="text-sm text-[#5A6B5C] space-y-1.5">
                        {item.configuration?.dexterity_name && <p><span className="font-semibold text-gray-800">Dexterity:</span> {item.configuration.dexterity_name}</p>}
                        {item.configuration?.iron_clubs?.length > 0 && (
                          <p>
                            <span className="font-semibold text-gray-800">Iron Set:</span>{' '}
                            {item.configuration.iron_clubs.join(', ')}{' '}
                            <span className="text-gray-500">
                              ({item.configuration.iron_clubs.length}{' '}
                              {item.configuration.iron_clubs.length === 1 ? 'club' : 'clubs'})
                            </span>
                          </p>
                        )}
                        {(item.configuration?.shaft_name || item.configuration?.shaft_manufacturer) && (
                          <div className="flex items-center gap-2">
                            {item.configuration?.shaft_image && (
                              <img src={item.configuration.shaft_image} alt="" className="w-8 h-6 object-cover rounded border border-gray-100" />
                            )}
                            <span>
                              <span className="font-semibold text-gray-800">Shaft:</span>{' '}
                              {item.configuration.shaft_manufacturer ? `${item.configuration.shaft_manufacturer} ` : ''}
                              {item.configuration.shaft_name || ''}
                            </span>
                          </div>
                        )}
                        {item.configuration?.shaft_flex_name && <p><span className="font-semibold text-gray-800">Flex:</span> {item.configuration.shaft_flex_name}</p>}
                        {item.configuration?.adapter_type_name && <p><span className="font-semibold text-gray-800">Club Type:</span> {item.configuration.adapter_type_name}</p>}
                        {item.configuration?.playing_length_name && <p><span className="font-semibold text-gray-800">Length:</span> {item.configuration.playing_length_name}</p>}
                        {(item.configuration?.grip_name || item.configuration?.grip_image) && (
                          <div className="flex items-center gap-2">
                            {item.configuration?.grip_image && (
                              <img src={item.configuration.grip_image} alt="" className="w-8 h-6 object-cover rounded border border-gray-100" />
                            )}
                            <span>
                              <span className="font-semibold text-gray-800">Grip:</span>{' '}
                              {item.configuration.grip_name || item.configuration.grip}
                            </span>
                          </div>
                        )}
                        {item.configuration?.tipping_name && <p><span className="font-semibold text-gray-800">Tipping:</span> {item.configuration.tipping_name}</p>}
                        {item.configuration?.pured_name && <p><span className="font-semibold text-gray-800">Pured:</span> {item.configuration.pured_name}</p>}
                        {item.configuration?.variant_selections &&
                          Object.values(item.configuration.variant_selections).map((sel) => (
                            <p key={sel.option_id}>
                              <span className="font-semibold text-gray-800">{sel.group_name}:</span>{' '}
                              {sel.option_name}
                              {Number(sel.price_modifier) > 0 && (
                                <span className="text-gray-500 ml-1">
                                  (+${Number(sel.price_modifier).toFixed(2)})
                                </span>
                              )}
                            </p>
                          ))}
                      </div>
                      <p className="text-xl font-bold text-[#4A6741] mt-3">${item.total_price?.toFixed(2)}</p>
                    </div>
                    
                    <button
                      onClick={() => handleRemoveItem(item.cart_item_id, index)}
                      className="text-[#5A6B5C] hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.cart_item_id || index}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
              
              <button
                onClick={handleClearCart}
                className="text-[#5A6B5C] hover:text-red-500 text-sm font-medium transition-colors"
                data-testid="clear-cart-button"
              >
                Clear Cart
              </button>
            </div>
            
            {/* Order Summary */}
            <div>
              <div className="bg-white p-6 rounded-lg border border-[#E8E4DC] sticky top-24">
                <h2 className="text-xl font-bold text-[#2C3E2D] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>Order Summary</h2>
                
                <div className="space-y-4 mb-6 pb-6 border-b border-[#E8E4DC]">
                  <div className="flex justify-between text-[#5A6B5C]">
                    <span>Subtotal</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#5A6B5C]">
                    <span>Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                </div>
                
                <div className="flex justify-between text-xl font-bold text-[#2C3E2D] mb-6">
                  <span>Total</span>
                  <span className="text-[#4A6741]" data-testid="cart-total">${total.toFixed(2)}</span>
                </div>
                
                <button
                  onClick={handleProceedToCheckout}
                  className="w-full h-12 px-8 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3A5733] transition-all"
                  data-testid="proceed-to-checkout-button"
                >
                  Proceed to Checkout
                </button>
                
                <Link to={tenantPath("/category/shafts")} className="block text-center text-[#5A6B5C] hover:text-[#4A6741] text-sm mt-4 transition-colors">
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}
